import { z } from 'zod';
import { PageKind, PageStatus } from '../enums';
import { LIMITS, RESERVED_SLUGS } from '../constants';
import { paginationQuerySchema, patchSchemaOf, seoSchema, uuidSchema } from './common';
import { richTextDocSchema } from './rich-text';
/**
 * A page slug may be nested (`insights/hiring-guide`), so it is validated as
 * one-or-more slug segments rather than as a single slug.
 */
export const pagePathSchema = z
    .string()
    .trim()
    .min(1)
    .max(LIMITS.slug.max)
    .transform((value) => value.replace(/^\/+|\/+$/g, ''))
    .refine((value) => value.split('/').every((seg) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(seg)), {
    error: 'Each path segment must be lowercase letters, numbers and single hyphens.',
})
    .refine((value) => !RESERVED_SLUGS.includes(value.split('/')[0] ?? ''), {
    error: 'That path is reserved by the application and would hide part of the site.',
});
/**
 * Only CUSTOM pages are creatable through the API.
 *
 * A STATIC page is a row that points at a React component; creating one from the
 * admin would produce a route that renders nothing. STATIC rows are introduced
 * by migrations alongside the component that serves them, and the admin can then
 * edit their slug, visibility, ordering and SEO — but not bring them into
 * existence.
 */
export const createPageSchema = z
    .object({
    title: z.string().trim().min(LIMITS.title.min).max(LIMITS.title.max),
    path: pagePathSchema,
    body: richTextDocSchema,
    status: z.enum(PageStatus).default(PageStatus.DRAFT),
    /** Whether the page resolves at all for anonymous visitors. */
    isVisible: z.boolean().default(false),
    parentId: uuidSchema.nullish(),
})
    .extend(seoSchema.shape);
/**
 * What may be changed on an existing page.
 *
 * `kind` and `componentKey` are absent by design — a CUSTOM page can never be
 * promoted to STATIC (there is no component to point at) and a STATIC page can
 * never be demoted (its component would be orphaned).
 */
const updatePageFields = z
    .object({
    title: z.string().trim().min(LIMITS.title.min).max(LIMITS.title.max),
    path: pagePathSchema,
    body: richTextDocSchema.nullish(),
    status: z.enum(PageStatus),
    isVisible: z.boolean(),
    parentId: uuidSchema.nullish(),
    sortOrder: z.number().int().min(0),
})
    .extend(seoSchema.shape).shape;
/**
 * Every field optional — and genuinely optional.
 *
 * `patchSchemaOf` rather than `.partial()`, because `seoSchema` contributes `noIndex` with
 * a `.default(false)` and `.partial()` would keep that default firing on a payload that
 * never mentioned it. See the note on `patchSchemaOf`.
 */
export const updatePageSchema = patchSchemaOf(updatePageFields);
/**
 * The bulk toggle behind the admin's "which pages are public?" screen — the
 * central control the client asked for. One request for the whole set keeps the
 * exposed surface coherent even if two admins save at once.
 */
export const setPageVisibilitySchema = z.object({
    pages: z
        .array(z.object({ id: uuidSchema, isVisible: z.boolean(), sortOrder: z.number().int().min(0) }))
        .min(1)
        .max(200),
});
export const adminPageQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().max(120).optional(),
    kind: z.enum(PageKind).optional(),
    status: z.enum(PageStatus).optional(),
    isVisible: z.coerce.boolean().optional(),
});
//# sourceMappingURL=page.js.map